import React, { createContext, useCallback, useState, useContext } from 'react';
import api from '../services/api';

import { formatPrice } from '../utils/funcoes';
import getResponseErrors from '../utils/getResponseErrors';
import { useToast } from './toast';

interface IProcedimento {
  ProCodigo: number;
  ProCodigoAlvaro: string;
  ProNome: string;
  ProDuracao: number;
  ProValor: number;
  ProValorPlano: number;
  ProValorPlano2: number;
  ProValorFormatado: string;
  ProValorPlanoFormatado: string;
  ProValorPlano2Formatado: string;
  ProCarencia: number;
  ProLimite: number;
}

interface IOptions {
  value: string;
  label: string;
  isDisabled?: boolean;
}

export interface IProcedimentosRetorno {
  procedimentos: IProcedimento[];
  optionsProcedimento: IOptions[];
}

interface AgendamentoContextData {
  loading: boolean;
  buscaProcedimentos(
    perfil: string,
    convenio?: string,
  ): Promise<IProcedimentosRetorno | undefined>;
}

const AgendamentoContext = createContext<AgendamentoContextData>(
  {} as AgendamentoContextData,
);

const AgendamentoProvider: React.FC = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  const buscaProcedimentos = useCallback(
    async (perfil: string, convenio?: string) => {
      setLoading(true);
      try {
        let url = `/procedimentos/perfil/${perfil}`;

        if (convenio)
          url = `/procedimentos/perfilconvenio/${perfil}/${convenio}`;

        const response = await api.get<IProcedimento[]>(url);

        const procedimentos = response.data.map(procedimento => {
          return {
            ...procedimento,
            ProValorFormatado: formatPrice(procedimento.ProValor),
            ProValorPlanoFormatado: formatPrice(procedimento.ProValorPlano),
            ProValorPlano2Formatado: formatPrice(procedimento.ProValorPlano2),
          };
        });

        const optionsProcedimento = response.data.map(procedimento => {
          return {
            value: procedimento.ProCodigo.toString(),
            label: procedimento.ProNome,
            isDisabled: false,
          };
        });
        return {
          procedimentos,
          optionsProcedimento,
        };
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar procedimentos',
          type: 'error',
          description: mensagemErro,
        });
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  return (
    <AgendamentoContext.Provider value={{ loading, buscaProcedimentos }}>
      {children}
    </AgendamentoContext.Provider>
  );
};

function useAgendamento(): AgendamentoContextData {
  const context = useContext(AgendamentoContext);

  if (!context) {
    throw new Error('useAgendamento deve ser usado com o AgendamentoProvider');
  }

  return context;
}

export { AgendamentoProvider, useAgendamento };
